import React, { useState, useRef, useMemo } from 'react'
import { type BreadcrumbItem, type ButtonVariant, ButtonWithMobileStyle } from '@valiant/ui-shared'
import { ChevronRight } from 'lucide-react'

export interface ButtonProps {
  label: string
  variant: ButtonVariant
  onClick: () => void
  icon?: React.ReactNode
}

export interface BadgeProps {
  node: React.ReactNode
  direction: string
}

interface BreadcrumbsCarouselProps {
  breadcrumbs?: BreadcrumbItem[]
  className?: string
}

export const BreadcrumbsCarousel = ({ breadcrumbs = [] }: BreadcrumbsCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(breadcrumbs.length - 1)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = (e: React.TouchEvent) => {
    if (breadcrumbs.length <= 1) return
    setIsDragging(true)
    setStartX(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return

    const currentX = e.touches[0].clientX
    const diff = currentX - startX
    setTranslateX(diff)
  }

  const handleTouchEnd = () => {
    if (!isDragging) return

    const threshold = 50 // Minimum distance to trigger slide

    if (Math.abs(translateX) > threshold) {
      if (translateX > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      } else if (translateX < 0 && currentIndex < breadcrumbs.length - 1) {
        setCurrentIndex(currentIndex + 1)
      }
    }

    setIsDragging(false)
    setTranslateX(0)
  }

  const handleMouseStart = (e: React.MouseEvent) => {
    if (breadcrumbs.length <= 1) return
    setIsDragging(true)
    setStartX(e.clientX)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return

    const diff = e.clientX - startX
    setTranslateX(diff)
  }

  const handleMouseEnd = () => {
    if (!isDragging) return

    const threshold = 50

    if (Math.abs(translateX) > threshold) {
      if (translateX > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      } else if (translateX < 0 && currentIndex < breadcrumbs.length - 1) {
        setCurrentIndex(currentIndex + 1)
      }
    }

    setIsDragging(false)
    setTranslateX(0)
  }

  const handleItemClick = (index: number, event: React.MouseEvent) => {
    // Prevent click if we were dragging
    if (isDragging) {
      event.preventDefault()
      event.stopPropagation()
      return
    }

    // Only allow clicking on non-current items
    if (index !== currentIndex) {
      setCurrentIndex(index)
    }
  }

  const getItemScale = (index: number) => {
    if (index === currentIndex) return 1
    if (Math.abs(index - currentIndex) === 1) return 0.85
    return 0.7
  }

  const getItemOpacity = (index: number) => {
    // Only the last item gets full opacity when in focus
    if (index === currentIndex && index === breadcrumbs.length - 1) return 1
    // All other items remain faded until clicked
    if (index === currentIndex) return 0.5
    if (Math.abs(index - currentIndex) === 1) return 0.4
    return 0.3
  }

  const getItemTranslateX = (index: number) => {
    const baseTranslate = (index - currentIndex) * 230
    return baseTranslate + (isDragging ? translateX : 0)
  }

  const handleImageError = (imageUrl: string) => {
    setFailedImages((prev) => new Set([...prev, imageUrl]))
  }
  const finalBreadcrumb = breadcrumbs[breadcrumbs.length - 1]

  const carouselHeight = useMemo(() => {
    const hasBadges = finalBreadcrumb?.badges && finalBreadcrumb?.badges.length > 0
    let height = 39
    if (hasBadges) height += 40

    return height
  }, [finalBreadcrumb])

  if (breadcrumbs.length === 0) {
    return null
  }

  return (
    <div>
      <div
        ref={containerRef}
        className={`flex items-start justify-center relative select-none w-full overflow-hidden`}
        style={{
          height: `${carouselHeight}px`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseStart}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseEnd}
        onMouseLeave={handleMouseEnd}
      >
        {/* Visible carousel items */}
        {breadcrumbs.map((breadcrumb, index) => (
          <div
            key={index + breadcrumb.label}
            className={`absolute transition-all duration-300 ease-out space-y-2 max-w-[240px] ${
              index !== currentIndex ? 'cursor-pointer' : ''
            }`}
            style={{
              transform: `translateX(${getItemTranslateX(index)}px) scale(${getItemScale(index)})`,
              opacity: getItemOpacity(index),
              zIndex: index === currentIndex ? 10 : Math.max(1, 5 - Math.abs(index - currentIndex)),
            }}
            onClick={(e) => handleItemClick(index, e)}
          >
            <div className="flex items-center justify-center gap-2 min-w-0 max-w-[240px]">
              {breadcrumb.imageUrl &&
              !failedImages.has(breadcrumb.imageUrl) &&
              !breadcrumb.hideImage ? (
                <img
                  src={breadcrumb.imageUrl}
                  alt={`${breadcrumb.label} logo`}
                  className="w-[31px] h-[31px] rounded object-cover"
                  onError={() => handleImageError(breadcrumb.imageUrl!)}
                  draggable={false}
                />
              ) : breadcrumb.fallbackImage ? (
                <div className="w-[31px] h-[31px] border border-card-border rounded-md bg-gray-200 flex items-center justify-center">
                  {breadcrumb.fallbackImage}
                </div>
              ) : !breadcrumb.hideImage ? (
                <div className="w-[31px] h-[31px] border border-card-border rounded-md bg-gray-200" />
              ) : (
                <></>
              )}
              {breadcrumb.icon ? breadcrumb.icon : null}

              <h1
                className={`text-lg md:text-3xl font-bold truncate whitespace-nowrap max-w-[180px] ${
                  index === currentIndex && breadcrumb.onClick
                    ? 'hover:cursor-pointer hover:underline'
                    : ''
                }`}
                onClick={index === currentIndex ? breadcrumb.onClick : undefined}
              >
                {breadcrumb.label}
              </h1>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                {breadcrumb.badges?.map((badge: BadgeProps) => badge.node)}
              </div>
            </div>
            {index !== breadcrumbs.length - 1 && (
              <div
                className="absolute top-[10px] flex items-center justify-center shrink-0"
                style={{
                  left: '50%',
                  transform: `translateX(${115}px) scale(${1 / getItemScale(index)})`,
                  zIndex: 5,
                }}
              >
                <ChevronRight className="h-4 w-4 md:h-6 md:w-6 shrink-0 " />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 w-full justify-center">
        {finalBreadcrumb.buttons?.map((button: ButtonProps) => (
          <ButtonWithMobileStyle
            key={button.label}
            variant={button.variant}
            onClick={button.onClick}
          >
            {button.icon ? button.icon : null}
            {button.label}
          </ButtonWithMobileStyle>
        ))}
      </div>
    </div>
  )
}
